import { IProduct } from 'services/dcs/bundle';
import { IAddBundleOptions, IProductModel } from 'components/Cart/models';
import { ByoSelectionItem, BYOSelectionState } from './sub-hooks/useBYOSelections';
import { IProductsWithSoftwarePlan } from 'hooks/useDCSTypes';
import { BYOItemProductsOrSoftwarePlans, ContentType } from 'api/contentful/contentTypes.cloverdotcom';
import { getProductBasePrice } from 'utils/DCSUtils';
import { BooleanFilter } from 'utils/TypeUtils';
import { getInstallmentPlanMap } from 'hooks/useDCS.utils';

export const getContentTypeFromContentfulProductsOrSoftware = (
  productsOrSoftwarePlans?: BYOItemProductsOrSoftwarePlans,
) => productsOrSoftwarePlans?.[0]?._sys.contentType || ContentType.PRODUCT_MODULE;

/**
 * Extracts the array of ByoSelectionItem objects from the given itemsMap.
 * @param {Record<string, ByoSelectionItem>} itemsMap - The object containing item types as keys and ByoSelectionItem arrays as values.
 * @returns {ByoSelectionItem[]} - The array of ByoSelectionItem objects, or an empty array if itemsMap is falsy.
 */
export const getItemsByType = (itemsMap: Record<string, ByoSelectionItem>) => {
  return itemsMap ? Object.values(itemsMap) : [];
};

/**
 * Maps an array of ByoSelectionItem objects to an array of products to add, using the provided getItemId function.
 * @param {ByoSelectionItem[]} items - The array of ByoSelectionItem objects.
 * @param {(getItemIds: ByoSelectionItem)} getItemIds - The function to get the ID of a product from a ByoSelectionItem.
 * @returns {{id: string; quantity: number}[]} - The array of products with their IDs and quantities.
 */
export const mapItemsToProductsToAdd = (
  items: ByoSelectionItem[],
  getItemIds: (item: ByoSelectionItem) => string[],
) => {
  return items.reduce((acc, item) => {
    const ids = getItemIds(item);
    const newProductsToAdd = ids
      .map(
        id =>
          id && {
            id,
            quantity: item.quantity,
          },
      )
      .filter(BooleanFilter);

    return [...acc, ...newProductsToAdd];
  }, []);
};

/**
 * Accumulates product quantities by merging products with the same ID and updating the quantity.
 * @param {{id: string; quantity: number}[]} accumulator - The array of accumulated products.
 * @param {{id: string; quantity: number}} product - The product to add to the accumulator.
 * @returns {{id: string; quantity: number}[]} - The updated array of accumulated products.
 */
export const accumulateProducts = (
  accumulator: { id: string; quantity: number }[],
  product: { id: string; quantity: number },
) => {
  const existingProduct = accumulator.find(item => item.id === product.id);

  if (existingProduct) {
    existingProduct.quantity += product.quantity;
  } else {
    accumulator.push(product);
  }

  return accumulator;
};

/**
 * Generates an array of ProductModel items based on the provided accessories and their quantities.
 * The function finds the matching products from the dcsProductList and aggregates the
 * accessory quantities for each product in the cart.
 *
 * @param {ByoSelectionItem[]} accessories - An array of accessories with their itemKey and quantity.
 * @param {IProduct[]} [dcsProductList=[]] - An array of products to find the matching accessory products.
 * @returns {IProductModel[]} - An array of ProductModel items with aggregated quantities.
 */
export const getCartProductsForAccessories = (
  accessories: ByoSelectionItem[],
  dcsProductList: IProduct[] = [],
): IProductModel[] => {
  return accessories.reduce((cartProducts: IProductModel[], accessory: ByoSelectionItem) => {
    // Create an array of accessory IDs based on the accessory quantity
    const accessoryIds = Array.from({ length: accessory.quantity }, () => accessory.dcsIds[0]);

    const accessoryProducts = accessoryIds.flatMap((accessoryId: string) => {
      const dcsProduct = dcsProductList.find(
        (product: IProduct) => product.id === accessoryId && product.isSelfServicePurchaseEnabled,
      );

      if (dcsProduct) {
        const productPrice = getProductBasePrice(dcsProduct);

        if (!productPrice) return [];

        return [
          {
            id: dcsProduct.id,
            fdmpId: dcsProduct.fdmpId,
            name: dcsProduct.name,
            totalCost: productPrice,
            isIncludedInMultiDevicePricing: dcsProduct.isIncludedInMultiDevicePricing,
            accessories: [],
            quantity: 1,
            installmentPlan: getInstallmentPlanMap(dcsProduct.pricing),
          },
        ];
      }
      return [];
    });

    accessoryProducts.forEach(accessoryProduct => {
      const existingCartProduct = cartProducts.find(({ id }) => id === accessoryProduct.id);
      if (existingCartProduct) {
        existingCartProduct.quantity++;
      } else {
        const { installmentPlan, ...accessoryProductWithoutInstallmentPlan } = accessoryProduct;
        cartProducts.push({ ...accessoryProductWithoutInstallmentPlan, installmentPlans: installmentPlan });
      }
    });

    return cartProducts;
  }, []);
};

/**
 * Processes step four by merging device and accessory products, accumulating their quantities,
 * and calling the addProductsWithSoftwarePlan function with the merged products and the softwarePlanId.
 */
export const tryAddBYOSystemToCart = (
  { POS_NEEDS, DEVICES, ACCESSORIES }: BYOSelectionState,
  addProductsWithSoftwarePlan: (
    productsWithSoftwarePlan: IProductsWithSoftwarePlan,
    accessories: IProductModel[],
    options?: IAddBundleOptions | undefined,
  ) => void,
  dcsProductList: IProduct[] = [],
) => {
  const devices = getItemsByType(DEVICES);
  const posNeeds = getItemsByType(POS_NEEDS);
  const accessories = getItemsByType(ACCESSORIES);

  const softwarePlanDcsId = posNeeds[0].dcsIds[0];

  if (devices.length > 0) {
    const deviceProductsToAdd = mapItemsToProductsToAdd(devices, item => item.dcsIds).reduce(
      accumulateProducts,
      [],
    );

    if (softwarePlanDcsId && deviceProductsToAdd.length > 0) {
      addProductsWithSoftwarePlan(
        {
          products: deviceProductsToAdd,
          planId: softwarePlanDcsId,
        },
        getCartProductsForAccessories(accessories, dcsProductList),
        {
          needsConfirm: true,
          needsRedirect: true,
        },
      );
    }
  }
};
